import { Box, Button, ButtonGroup, CircularProgress, Collapse, Dialog, Drawer, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Comment } from '@mui/icons-material'
import { useParams } from 'react-router-dom'
import readXlsxFile from 'read-excel-file'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import dayjs from 'dayjs'

import { Icons, RED_SHADE_TABLE_HIGHLIGHT_COLOR, SPACES_LOCATION_REGEXP, boundaryValue, convertToCDNLink, exportAssignmentReport, formatDate, isIOS } from '../../utils/utilities'
import AssignmentSubmissionDownloadDialog from '../../components/presenter/AssignmentSubmissionDownloadDialog'
import CustomFileInputButton from '../../components/common/CustomFileInputButton'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import CustomTable from '../../components/common/custom-table/CustomTable'
import DocumentViewer from '../../components/common/DocumentViewer'
import BreadcrumbList from '../../components/common/BreadcrumbList'
import { useAssignmentStore } from '../../store/assignment.store'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import CustomTooltip from '../../components/common/CustomTooltip'
import noSubmissionImg from '../../assets/nosubmissions.svg'
import CountCard from '../../components/common/CountCard'
import api from '../../service/api'

const rowStylesGetter = row => {
    if ( row.no_submission ) {
        return {
            "--bg-color": t => t.theme === 'dark' ? t.darken( RED_SHADE_TABLE_HIGHLIGHT_COLOR, 0.5 ) : t.lighten( t.palette.error.main, 0.8 ),
            ":hover": {},
            cursor: "default",
            "& td": {
                color: t => `${t.lighten( t.palette.text.secondary, 0.3 )} !important`
            }
        }
    }
    else return {}
}

const ViewerSubmission = ( { setSelectedSubmissionForAction, submission, viewSubmission, selected } ) => {

    const [collapseState, setCollapseState] = useState( false )
    const [downloading, setDownloading] = useState( false )

    const AssignmentStore = useAssignmentStore()

    const { palette, border } = useTheme()



    const downloadSubmission = async ( value ) => {
        setDownloading( value )
        await AssignmentStore.downloadAssignment( value )
        setDownloading( null )
    }

    const handleClickOnSubmission = ( item, index ) => {
        const submissionFileId = [+submission.assignment_submission_id, index, +submission.id - 1]
        setSelectedSubmissionForAction( submission )

        if ( item.endsWith( "pdf" ) || !isIOS() ) {
            viewSubmission( item, submissionFileId )
        }
        else toast( `View option not supported for this format on this device` )
    }

    return (
        <Box borderBottom={border[1]} key={submission.assignment_submission_id}>
            <Box sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} onClick={() => setCollapseState( p => !p )} padding="10px 20px" display="flex" gap="20px" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography color={+selected[0] === +submission.assignment_submission_id && palette.primary.main} variant='subtitle2'>{submission.student_name}</Typography>
                    <Typography color={+selected[0] === +submission.assignment_submission_id && palette.primary.main} variant='body2' fontSize="12px">{submission.assignment_submitted_by}</Typography>
                </Box>
                {Icons.default.KeyboardArrowDownIcon}
            </Box>
            <Collapse in={collapseState || +submission.assignment_submission_id === +selected[0]}>
                <Box borderTop={border[1]}>
                    {submission?.assignment_submission_file_paths?.map( ( item, index ) => {
                        const submissionSelected = +selected[0] === +submission.assignment_submission_id && +selected[1] === index
                        const fileDetails = item.match( SPACES_LOCATION_REGEXP ).groups
                        return (
                            <Box onClick={() => handleClickOnSubmission( item, index )} justifyContent="space-between" display="flex" alignItems="center" padding="5px 20px 5px 40px" borderBottom={border[1]} color={palette.secondary.main} gap="20px" key={item}>
                                <Typography color={submissionSelected ? palette.primary.main : "textSecondary"} fontSize="14px" maxWidth="80%" sx={{ wordBreak: "break-all", wordWrap: "break-word", fontWeight: selected ? "500" : "300", fontStyle: selected ? "italic" : "normal", cursor: "pointer", ":hover": { color: !submissionSelected && palette.common.font } }} display="flex" gap="10px" alignItems="center" >
                                    {index + 1}. {decodeURIComponent( fileDetails.filefullname )}
                                </Typography>
                                <Box flexShrink={0}>
                                    <IconButton size='small' onClick={() => downloadSubmission( item )}>
                                        {downloading === item ? <CircularProgress size={14} /> : Icons.small.DownloadIcon}
                                    </IconButton>
                                </Box>
                            </Box>
                        )
                    } )}
                </Box>
            </Collapse>
        </Box>
    )
}

const EvaluateAssignment = observer( () => {
    let { assignment_id } = useParams()

    const AssignmentStore = useAssignmentStore()

    const [open, setOpen] = React.useState( false )
    const [loaded, setLoaded] = React.useState( false )
    const [assignedScore, setAssignedScore] = useState( 0 )
    const [legacyGroup, setLegacyGroup] = useState( false )
    const [deleting, setDeleting] = React.useState( false )
    const [importedData, setImportedData] = React.useState( [] )
    const [previewFile, setPreviewFile] = React.useState( null )
    const [dialogType, setDialogType] = React.useState( 'download' ) // download || setmarks
    const [showImportSteps, setShowImportSteps] = React.useState( null )
    const [assignmentDetails, setAssignmentDetails] = React.useState( {} )
    const [bulkUploadingScores, setBulkUploadingScores] = useState( false )
    const [pageWidthAndScale, setPageWidthScaleFactor] = React.useState( 1 )
    const [loadingSubmission, setLoadingSubmission] = React.useState( false )
    const [selectedSubmission, setSelectedSubmission] = React.useState( null )
    const [previewingFileName, setPreviewingFileName] = React.useState( null )
    const [selectedForDeletion, setSelectedForDeletion] = React.useState( null )
    const [selectedSubmissionForAction, setSelectedSubmissionForAction] = React.useState( [] )
    const [showOfflineAssignmentDetails, setShowOfflineAssignmentDetails] = React.useState( false )
    const [assignedStudents, setAssignedStudents] = useState( [] )

    const { palette, border, layout } = useTheme()

    const displaySubmissions = useMemo( () => {
        const submitted = AssignmentStore.getSubmission || []
        const getStudentId = student => student.assignment_submitted_by || student.auid || ""
        const getUsn = student => student.student_usn || ''
        const submittedIds = new Set( submitted.map( submission => String( getStudentId( submission ) ) ) )
        const unsubmitted = assignedStudents
            .filter( student => !submittedIds.has( String( getStudentId( student ) || "" ) ) )
            .map( student => ( {
                id: `unsubmitted-${student.auid}`,
                student_name: student.student_name || "Not Available",
                auid: student.auid || student.auid,
                assignment_submitted_by: student.auid || student.auid,
                student_usn: student.usn,
                no_submission: true,
            } ) )

        const sortByUsn = ( first, second ) => String( getUsn( first ) ).localeCompare( String( getUsn( second ) ), undefined, { numeric: true, sensitivity: "base" } )
        return [...submitted].sort( sortByUsn ).concat( unsubmitted.sort( sortByUsn ) )
    }, [AssignmentStore.getSubmission, assignedStudents] )

    const importReviewTableColumns = useMemo( () => {
        return ( [
            { id: 2, field: "student_name", name: "Student Name", padding: "5px 20px 5px 20px" },
            { id: 3, field: "auid", sortable: true, name: "AUID", padding: "5px 20px 5px 20px" },
            { id: 4, field: "usn", sortable: true, name: "USN", padding: "5px 20px 5px 20px" },
            {
                id: 5, field: "marks", padding: "5px 20px 5px 20px", name: "Marks", renderCell: ( row, index ) => {
                    return (
                        <TextField type='number' slotProps={{
                            htmlInput: { min: 0 }
                        }} value={row.marks || 0} onChange={event => {
                            setImportedData( prev => {
                                const newData = [...prev]
                                newData[index].marks = event.target.value
                                return newData
                            } )
                        }} size='small' />
                    )
                }
            },
        ] )
    }, [] )

    const handleClose = () => {
        setOpen( null )
    }

    const handlePdfPageResize = useCallback( ( delta, setToFit ) => {
        if ( setToFit )
            return setPageWidthScaleFactor( { scale: 1, width: window.innerWidth > 600 ? '' : window.innerWidth - 100 } )
        setPageWidthScaleFactor( prev => {
            const newScale = boundaryValue( 0.5, ( prev.scale || 1 ) + delta, 2 )
            return { scale: newScale, width: window.innerWidth > 600 ? '' : window.innerWidth - 100 }
        } )
    }, [] )

    const viewSubmission = async ( value, id ) => {
        setLoadingSubmission( true )
        try {
            const { data: { data } } = await api.downloadAssignment( value, true )
            const fileDetails = value.match( SPACES_LOCATION_REGEXP ).groups
            setPreviewingFileName( decodeURIComponent( fileDetails.filefullname ) )
            setPreviewFile( convertToCDNLink( data ) )
            setSelectedSubmission( id )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || `An error occurred while viewing the submission!` )
        } finally {
            setLoadingSubmission( false )
        }
    }

    const openDownloadDialog = useCallback( ( e, value ) => {
        viewSubmission( value?.assignment_submission_file_paths?.at( 0 ), [+value.assignment_submission_id, 0, +value.id - 1] )
        setSelectedSubmissionForAction( value )
    }, [] )

    const deleteSubmission = async () => {
        setDeleting( true )
        await AssignmentStore.deleteAssignmentSubmission( selectedForDeletion )
        setSelectedForDeletion( null )
        setDeleting( false )
    }

    const openMarksAssignDialog = useCallback( ( e, value ) => {
        setDialogType( 'setmarks' )
        setSelectedSubmissionForAction( value )
        setOpen( e.currentTarget )
    }, [] )

    const onImportFileChange = async event => {
        try {
            const file = event.type === "drop" ? event.dataTransfer.files[0] : event.target.files[0]
            if ( file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' )
                return toast( "Please select a Excel(.xlsx) file. Other formats are not supported" )

            const excelData = await readXlsxFile( file )
            let headers = excelData[0]
            const formattedData = []
            if ( headers ) {
                headers = headers.map( head => head?.toLowerCase()?.replace( /[^0-9a-zA-Z]/g, "_" ) )
                const requiredFields = ['auid', 'student_name', 'usn', 'marks']
                if ( requiredFields.every( requiredField => headers.includes( requiredField ) ) ) {
                    const fieldIndices = requiredFields.map( field => headers.findIndex( header => header === field ) )

                    const data = excelData.slice( 1 )
                    for ( const row of data ) {
                        const details = {}
                        requiredFields.forEach( ( field, index ) => {
                            details[field] = row[fieldIndices[index]]
                        } )
                        formattedData.push( details )
                    }
                    setImportedData( formattedData )


                } else {
                    toast( "The Excel file does not contain the data in the required format!" )
                }
            }

        } catch ( err ) {
            console.log( err )
        } finally {
            event.target.value = ''
        }
    }

    const importExtractedExcelData = async () => {
        setBulkUploadingScores( true )
        try {
            let payload = { "assignment_id": assignment_id, "scores": importedData }
            if ( await AssignmentStore.bulkUploadAssignmentScore( payload ) ) {
                await AssignmentStore.fetchAssignmentSubmission( assignment_id )
                setImportedData( [] )
                setShowOfflineAssignmentDetails( false )
            }
        } catch ( err ) {
            toast( "Something went wrong while bulk uploading assignment scores." )
        }
        setBulkUploadingScores( false )
    }

    const nextSubmission = () => {
        if ( selectedSubmission ) {
            const [subId, attIndex, subIndex] = selectedSubmission || []
            const currentSubmissions = AssignmentStore.getSubmission[subIndex]?.assignment_submission_file_paths

            if ( currentSubmissions?.length - 1 === attIndex ) {
                if ( AssignmentStore.getSubmission?.length - 1 > subIndex + 1 ) {
                    const nextSub = AssignmentStore.getSubmission[subIndex + 1]
                    const nextFile = nextSub?.assignment_submission_file_paths[0]
                    viewSubmission( nextFile, [nextSub.assignment_submission_id, 0, nextSub.id - 1] )
                    setSelectedSubmissionForAction( nextSub )
                } else if ( AssignmentStore.getSubmission?.length === subIndex + 1 ) {
                    const nextSub = AssignmentStore.getSubmission[0]
                    const nextFile = nextSub?.assignment_submission_file_paths[0]
                    viewSubmission( nextFile, [nextSub.assignment_submission_id, 0, nextSub.id - 1] )
                    setSelectedSubmissionForAction( nextSub )
                }
            } else if ( currentSubmissions?.length - 1 > attIndex ) {
                const nextSub = AssignmentStore.getSubmission[subIndex]
                const nextFile = nextSub?.assignment_submission_file_paths[attIndex + 1]
                viewSubmission( nextFile, [nextSub.assignment_submission_id, attIndex + 1, nextSub.id - 1] )
                setSelectedSubmissionForAction( nextSub )
            }
        }
    }

    const prevSubmission = () => { }

    const submitScore = async ( e, event ) => {
        const value = e.target.value

        if ( value === selectedSubmissionForAction?.assignment_marks ) return
        if ( event === 'blur' || ( e.key === 'Enter' ) ) {
            let payload = { "assignment_submission_id": selectedSubmissionForAction.assignment_submission_id, "assignment_marks": value }
            if ( await AssignmentStore.setAssignmentScore( payload ) ) {
                setAssignedScore( value )
                setSelectedSubmissionForAction( prev => {
                    const newValue = { ...prev }
                    newValue.assignment_marks = value
                    return newValue
                } )
            } else
                setAssignedScore( selectedSubmissionForAction?.assignment_marks || 0 )
        }
    }

    const openReMarksAssignDialog = useCallback( ( e, value ) => {
        setDialogType( 'setremarks' )
        setSelectedSubmissionForAction( value )
        setOpen( e.currentTarget )
    }, [] )

    const assignmentTableColumns = useMemo( () => {
        return [
            {
                id: 1,
                width: "180px",
                sortable: true,
                filterable: true,
                field: "student_name",
                name: "Student Name",
                padding: "5px 20px",
                cellStyles: { textTransform: "capitalize" },
                renderCell: ( row ) => (
                    row.student_name ? row.student_name?.toLowerCase() : (
                        <Typography gap="10px" display="inline-flex" alignItems="center" color='textSecondary' fontSize="12px">
                            Not Available
                            <CustomTooltip placement="top-start" title="Name not available! Please check AUID if imported from excel file.">
                                {Icons.default.Info}
                            </CustomTooltip>
                        </Typography>
                    )
                )
            },
            {
                id: 2,
                field: "student_id",
                sortable: true,
                filterable: true,
                name: "AUID",
                width: "140px",
                padding: "5px 20px",
                renderCell: row => row.assignment_submitted_by || "-"
            },
            {
                id: 3,
                field: "student_usn",
                sortable: true,
                filterable: true,
                name: "USN",
                width: "140px",
                padding: "5px 20px"
            },
            {
                id: 4,
                field: "assignment_submitted_on",
                width: "140px",
                name: "Submitted On",
                padding: "5px 20px",
                renderCell: ( row ) => (
                    row.assignment_submitted_on ? <Box display="flex" gap="5px" alignItems="center">
                        <Box display="flex" alignItems="center" fontSize="18px" component="span">{Icons.default.Calendar}</Box>
                        <Typography component="span" fontSize="12px">
                            {formatDate( row.assignment_submitted_on, { format: "lll" } )}
                        </Typography>
                    </Box> : "NA"
                )
            },
            {
                id: 5,
                field: "assignment_marks",
                sortable: true,
                filterable: true,
                name: "Marks",
                align: "center",
                padding: "5px 20px",
                renderCell: ( row ) => row.assignment_marks || "-"
            },
            {
                id: 6,
                field: "assignment_remarks",
                name: "Remarks",
                align: "center",
                padding: "5px 20px",
                renderCell: ( row ) => (
                    <Typography sx={{ maxWidth: '150px' }} display="block" component="strong" fontSize="12px" noWrap>
                        {row.no_submission ? "Not Submitted" : row.assignment_remarks || "-"}
                    </Typography>
                )
            },
            {
                id: 7,
                width: "320px",
                field: "actions",
                name: "Actions",
                align: "right",
                padding: "5px 20px",
                renderCell: ( row ) => (
                    <Box display="flex" justifyContent="flex-end" alignItems="center" gap="10px">
                        {!row.no_submission && assignmentDetails.assignment_submission_mode !== 'offline' && (
                            <Button
                                onClick={e => openDownloadDialog( e, row )}
                                variant='outlined'
                                sx={{ fontSize: "12px", textTransform: "capitalize" }}
                                size="small"
                                color="primary"
                            >
                                View submissions
                            </Button>
                        )}
                        <Button
                            sx={{ fontSize: "12px", textTransform: "capitalize" }}
                            onClick={e => openMarksAssignDialog( e, row )}
                            size="small"
                            variant='outlined'
                            color="success"
                            disabled={row.no_submission && assignmentDetails.assignment_submission_mode !== 'offline'}
                        >
                            Set Score
                        </Button>
                        <Tooltip disableInteractive title="Add remarks">
                            <IconButton disabled={row.no_submission && assignmentDetails.assignment_submission_mode !== 'offline'} onClick={e => openReMarksAssignDialog( e, row )} size="small" color='secondary'>
                                <Comment sx={{ fontSize: "18px" }} />
                            </IconButton>
                        </Tooltip>
                        {!row.no_submission && <Tooltip disableInteractive title="Delete this submission">
                            <IconButton onClick={e => setSelectedForDeletion( row )} size="small" color="error">
                                {Icons.default.DeleteIconOutlined}
                            </IconButton>
                        </Tooltip>}
                    </Box>
                )
            }
        ]
    }, [assignmentDetails, openDownloadDialog, openMarksAssignDialog, openReMarksAssignDialog] )


    useEffect( () => {
        setAssignedScore( selectedSubmissionForAction?.assignment_marks || 0 )
    }, [selectedSubmissionForAction] )

    useEffect( () => {
        const getData = async () => {
            const { assignment = {} } = await AssignmentStore.fetchAssignmentSubmission( assignment_id )

            setAssignmentDetails( assignment )
            const groupIds = String( assignment?.assignment_batch_id || "" )
                .split( "," )
                .map( groupId => groupId.trim() )
                .filter( Boolean )

            if ( groupIds.length > 0 ) {
                try {
                    const studentLists = await Promise.all( groupIds.map( groupId => {
                        const params = {}
                        if ( String( groupId ).includes( "-" ) )
                            params.section_assignment_id = String( groupId ).split( "-" )[0]
                        else
                            params.batch_assignment_id = groupId

                        return api.quiz.fetchBatchOrSectionAssignedStudentDetails( params )
                    } ) )

                    const students = studentLists.flatMap( response => {
                        const responseData = response?.data?.data
                        if ( Array.isArray( responseData ) ) return responseData
                        return responseData?.students || []
                    } )

                    if ( students.length === 0 ) {
                        setLegacyGroup( true )
                    }

                    const uniqueStudents = Array.from( new Map( students.map( student => [String( student.auid || student.student_id || student.id ), student] ) ).values() )
                    setAssignedStudents( uniqueStudents )
                } catch ( err ) {
                    console.log( err )
                    setAssignedStudents( [] )
                }
            }
            if ( assignment.assignment_submission_mode !== 'online' && AssignmentStore.getSubmission.length === 0 ) {
                setShowOfflineAssignmentDetails( true )
            }
            setLoaded( true )
        }
        getData()
    }, [AssignmentStore, assignment_id] )


    return (
        <Box flexGrow={1} display="flex" flexDirection="column" overflow="auto" padding={layout.pagePadding}>

            <Drawer anchor='bottom' sx={{ zIndex: 9000000 }} open={Boolean( previewFile )} slotProps={{
                paper: { sx: { height: "calc(100% - 50px)" } }
            }} >
                <Box bgcolor={palette.cardBackground} flexGrow={1} display="flex" overflow="auto" flexDirection="column" borderRadius="20px 20px 0 0">
                    <Box borderBottom={border[1]} flexWrap="wrap" display="flex" justifyContent="space-between" gap="20px" padding="10px 20px">
                        <Box display="flex" alignItems="center" gap="10px" >
                            <IconButton onClick={() => setPreviewFile( null )}>
                                {Icons.default.ArrowBack}
                            </IconButton>
                            <Box>
                                <Typography flexGrow={1} variant='h6' sx={{ wordBreak: "break-all", whiteSpace: "preserve-breaks" }} fontSize="14px">{previewingFileName}</Typography>
                                <Typography flexGrow={1} fontWeight="300" sx={{ wordBreak: "break-all", whiteSpace: "preserve-breaks", textTransform: "capitalize" }} fontSize="14px">By <u><em>{selectedSubmissionForAction.student_name?.toLowerCase()}</em></u></Typography>
                            </Box>
                        </Box>
                        <Box display="flex" alignItems="center" gap="10px" >
                            {['pdf', 'jpeg', 'jpg'].includes( previewingFileName?.split( "." ).pop()?.toLowerCase() ) && <Box display="flex" alignItems="center" gap="5px">
                                <IconButton id="pdf-doc-zoom-out-btn" onClick={() => handlePdfPageResize( -0.1 )}>
                                    {Icons.small.ZoomOut}
                                </IconButton>
                                <Typography bgcolor={palette.contentBg} fontSize="12px" padding="5px" borderRadius="5px"  >{`${Math.round( ( pageWidthAndScale.scale || 1 ) * 100 )}%`}</Typography>
                                <IconButton id="pdf-doc-zoom-in-btn" onClick={() => handlePdfPageResize( 0.1 )}>
                                    {Icons.small.ZoomIn}
                                </IconButton>
                                <IconButton id="pdf-doc-fit-btn" onClick={() => handlePdfPageResize( 1, true )}>
                                    {Icons.small.CenterFocusStrongOutlined}
                                </IconButton>
                            </Box>}
                            <Box position="relative" sx={{ zIndex: 8000000 }}>
                                <TextField size='small' onBlur={e => submitScore( e, 'blur' )} onKeyUp={e => submitScore( e, 'keyup' )} onChange={e => setAssignedScore( e.target.value )} onFocus={e => e.target.select()} color="secondary" value={assignedScore || 0} type="number" slotProps={{
                                    input: { startAdornment: <Typography color="secondary" paddingRight="20px" variant='subtitle2'>SCORE:</Typography>, inputProps: { min: 0 } }
                                }} />
                            </Box>
                            <IconButton id="pdf-doc-fit-btn" onClick={() => setPreviewFile( null )}>
                                {Icons.small.CloseIcon}
                            </IconButton>
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="column" overflow="auto" className="custom-scrollbar" flexGrow={1}>
                        <Box display="grid" gridTemplateColumns={{ md: "350px auto", xs: "1fr" }} flexGrow={1} overflow="auto" >
                            <Box display={{ md: "block", xs: "none" }} overflow="auto">
                                {AssignmentStore.getSubmission?.map( ( submission ) => (
                                    <ViewerSubmission selected={selectedSubmission} setSelectedSubmissionForAction={setSelectedSubmissionForAction} viewSubmission={viewSubmission} submission={submission} key={submission.assignment_submission_id} />
                                ) )}
                            </Box>
                            <Box position="relative" bgcolor={palette.contentBg} overflow="auto" flexShrink={0} height="100%" flexDirection="grid" gridTemplateRows="1fr" alignItems="center" justifyContent="center" flexGrow={1}>
                                {loadingSubmission ? <Box display="flex" justifyContent="center">
                                    <DataLoadingSpinner waitingMessage="Loading submission file..." />
                                </Box> : <DocumentViewer scale={pageWidthAndScale.scale} width={pageWidthAndScale.width} type={previewingFileName?.split( "." ).pop()} setFile={setPreviewFile} file={previewFile} />}
                                <Box sx={{ position: "fixed", bottom: "20px", clipPath: "inset(0px)", zIndex: 100 }} gap="10px" padding="0 20px" display="flex" justifyContent="space-between">
                                    <IconButton onClick={prevSubmission} sx={{ border: border[1], bgcolor: palette.common.bg + "66", color: palette.common.font, ":hover": { bgcolor: palette.common.bg + "99" } }}>
                                        {Icons.default.KeyBoardArrowLeft}
                                    </IconButton>
                                    <IconButton onClick={nextSubmission} sx={{ border: border[1], bgcolor: palette.common.bg + "66", color: palette.common.font, ":hover": { bgcolor: palette.common.bg + "99" } }}>
                                        {Icons.default.KeyBoardArrowRight}
                                    </IconButton>
                                </Box>
                            </Box>

                        </Box>

                    </Box>

                </Box>
            </Drawer>


            <Dialog open={importedData.length > 0}>
                <Box padding="20px">
                    <Typography fontSize="16px" marginBottom="20px" variant='h6' component="h1">Review the imported data before confirming the import</Typography>
                    <CustomTable tableKey="imprev" data={importedData} columns={importReviewTableColumns} />
                    <Box display="flex" gap={layout.cardsGap} justifyContent="flex-end" marginTop="20px">
                        <Button startIcon={Icons.default.CloseIcon} variant='outlined' color='error' disableElevation size='small' onClick={() => setImportedData( [] )}>Cancel</Button>
                        <Button disabled={bulkUploadingScores} startIcon={bulkUploadingScores ? <CircularProgress size={20} /> : Icons.default.DownloadIcon} variant='contained' color='primary' disableElevation size='small' onClick={importExtractedExcelData}>{bulkUploadingScores ? "Importing marks..." : "Import Marks"}</Button>
                    </Box>
                </Box>
            </Dialog>

            <Dialog open={showImportSteps} onClose={() => setShowImportSteps( false )}>
                <Box padding="20px">
                    <Typography fontSize="18px" marginBottom="20px" variant='h6' component="h1">How to Import Marks from Excel</Typography>
                    <Box component="ol" sx={{ marginLeft: "20px" }}>
                        <Typography fontSize="14px" component="li"><strong>Prepare your file:</strong> Create an Excel file with a name of your choice.</Typography>
                        <Typography fontSize="14px" component="li">
                            <strong>Set up the headers:</strong> On the first sheet of the file, include the following column headers in the first row. These are <strong>case-insensitive</strong> and can be in <strong>any order</strong>:
                            <Box component="ul" sx={{ marginLeft: "20px" }}>
                                <Typography fontSize="14px" component="li">AUID</Typography>
                                <Typography fontSize="14px" component="li">Student Name</Typography>
                                <Typography fontSize="14px" component="li">Marks</Typography>
                                <Typography fontSize="14px" component="li">USN(Leave empty if not available)</Typography>
                            </Box>
                        </Typography>
                        <Typography fontSize="14px" component="li"><strong>Upload the data:</strong> Enter the student marks into the corresponding rows, then click the <em>Import from Excel</em> button to upload your data.</Typography>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" marginTop="20px">
                        <Button variant='contained' color='primary' disableElevation size='small' onClick={() => setShowImportSteps( false )}>Okay</Button>
                    </Box>
                </Box>
            </Dialog>

            <AssignmentSubmissionDownloadDialog setPreviewFile={setPreviewFile} previewFile={previewFile} viewSubmission={viewSubmission} dialogType={dialogType} open={open} selectedValue={selectedSubmissionForAction} onClose={handleClose} />

            {selectedForDeletion && <ConfirmDialog actionName="Yes! delete it" status={Boolean( selectedForDeletion )} confirmAction={deleteSubmission} waiting={deleting} cancelAction={() => setSelectedForDeletion( null )} message={`Are you sure you want delete submission of ${selectedForDeletion.student_name}`} />}
            {/* Breadcrumb */}
            <BreadcrumbList items={[
                { label: "Assignments", link: "/faculty/assignment" },
                !loaded ? <CircularProgress size={14} /> : assignmentDetails.assignment_name,
                "Submissions"
            ]} />


            {/* Content */}
            <Box gap="20px" display="flex" flexDirection="column" overflow={{ md: "auto", xs: "initial" }} >
                <Box display="flex" gap="20px" alignItems={{ sm: "center", xs: "flex-start" }} justifyContent="space-between" flexDirection={{ sm: "row", xs: "column" }}>
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{assignmentDetails?.assignment_name ? assignmentDetails?.assignment_name?.endsWith( "s" ) ? `${assignmentDetails?.assignment_name}' ` : `${assignmentDetails?.assignment_name}'s ` : ""}Submissions</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary'  >Manage assignment submissions.</Typography>
                    </Box>
                </Box>
                <Box display="grid" gap="20px" width="100%" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "repeat(2,1fr)" }}>
                    <CountCard label='Due Date' icon={Icons.default.Calendar} value={dayjs( assignmentDetails.assignment_due_date ).format( "DD MMM YYYY, hh:mm A" )} />
                    <CountCard label='Submissions' icon={Icons.default.Publish} value={AssignmentStore.getSubmission.length} />
                    <CountCard label='Evaluated' icon={Icons.default.CheckCircleIcon} value={AssignmentStore.getSubmission?.reduce( ( acc, sub ) => acc += sub.assignment_marks ? 1 : 0, 0 )} />
                </Box  >

                {/* Offline assignment marks import details */}
                <Box display="flex" flexDirection="column" gap={layout.cardsGap}>
                    {loaded && showOfflineAssignmentDetails && assignmentDetails.assignment_submission_mode === 'offline' && <Box border={`1px solid ${palette.info.dark}`} bgcolor={palette.info.light + "11"} borderRadius={layout.borderRadius} flexDirection="column" padding="10px" display="flex" gap={layout.cardsGap}>
                        <Box alignItems="flex-start" gap={layout.cardsGap} display="flex">
                            <Typography fontSize="20px" marginTop="2px" color='info' component="span">{Icons.default.Info}</Typography>
                            <Box>
                                <Typography component="h3" variant='h6' fontSize="16px" >This assignment submissions mode is <strong>offline</strong></Typography>
                                <Typography component="p" variant='caption' color='textPrimary' fontSize="12px" >This assignment submission mode is set to offline, that means the students should submit the assignments directly to faculty completed in yellow books or any method specified by the faculty.</Typography>
                                <Typography component="p" variant='caption' color='textPrimary' fontSize="12px" >Faculty members can import the students marks for this assignment using the excel file.</Typography>
                            </Box>
                        </Box>
                        <Box display="flex" gap={layout.cardsGap} flexWrap="wrap" alignItems="center" marginTop="10px">
                            <CustomFileInputButton accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx' onChange={onImportFileChange} color='secondary' variant='contained' size='small' disableElevation startIcon={Icons.default.DownloadForOfflineIcon}>Import marks from Excel</CustomFileInputButton>
                            <Button sx={{ flexGrow: { xs: 1, sm: "initial" } }} startIcon={Icons.default.Info} onClick={() => setShowImportSteps( true )} color='secondary' variant='outlined' size='small'>Learn how to import from excel</Button>
                            <Button sx={{ flexGrow: { xs: 1, sm: "initial" } }} color='info' variant='outlined' onClick={() => setShowOfflineAssignmentDetails( false )} size='small'>dismiss</Button>
                        </Box>
                    </Box>}

                    {/* Action Buttons */}
                    {loaded && ( ( !showOfflineAssignmentDetails && assignmentDetails.assignment_submission_mode === 'offline' ) || AssignmentStore.getSubmission.length > 0 ) && <Box display="flex" flexWrap="wrap" gap={layout.cardsGap} >
                        {loaded && !showOfflineAssignmentDetails && assignmentDetails.assignment_submission_mode === 'offline' &&
                            <ButtonGroup size='small'>
                                <CustomFileInputButton accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx' onChange={onImportFileChange} color='secondary' variant='contained' size='small' disableElevation startIcon={Icons.default.DownloadForOfflineIcon}>Import marks from Excel</CustomFileInputButton>
                                <CustomTooltip placement="top" disableInteractive title="Learn how to import marks from excel sheet" ><Button onClick={() => setShowImportSteps( true )} color='secondary' disableElevation variant='outlined' sx={{ fontSize: "18px" }}>{Icons.default.Info}</Button></CustomTooltip>
                            </ButtonGroup>
                        }
                    </Box>}


                    {legacyGroup && <Typography sx={{ m: 0.5, fontSize: "0.875rem", display: "flex", gap: 1, alignItems: "flex-start", p: 1, borderRadius: layout.borderRadius, bgcolor: palette.warning.light + "22", color: palette.warning.main }} >
                        <Typography component="span" sx={{ color: "inherit", fontSize: "1.2rem" }} >{Icons.default.Warning}</Typography>
                        The batch or section assigned to this resource belongs to a previous academic year and is no longer active, so the list of students who did not attempt this exam cannot be displayed.
                    </Typography>}

                    {/* Data Table */}
                    {loaded && displaySubmissions.length > 0 && <CustomTable
                        rowStyles={rowStylesGetter}
                        tableKey="assignsubs"
                        stickyHeader
                        data={displaySubmissions}
                        columns={assignmentTableColumns}
                        customActions={[
                            { label: "Export To Excel", variant: "contained", color: "primary", type: "button", startIcon: Icons.default.UploadFileIcon, onClick: ( rows ) => exportAssignmentReport( rows, legacyGroup ) }
                        ]}
                    />}
                    {!loaded && <DataLoadingSpinner waitingMessage="Loading submissions..." />}
                    {loaded && displaySubmissions.length === 0 && <Box padding="20px" flexGrow="1" borderRadius="5px" bgcolor={palette.contentBg} display="flex" flexDirection="column" alignItems="center" gap="10px" color="textSecondary" variant='h6' justifyContent="center" textAlign="center" >
                        <img src={noSubmissionImg} width="200px" alt="No submissions!" />
                        <Typography variant='subtitle2' marginTop="10px" color="textSecondary" >No submissions received yet! Please wait for the students to upload their submissions.</Typography>
                    </Box>}
                </Box>
            </Box>
        </Box>
    )
} )

export default EvaluateAssignment
